import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import Close from '@mui/icons-material/Close';
import { getMessagesForTopic } from '../../../utils/MessageHistory.js';
import { ChatComponentProps, Tab } from '../../../types/chat';
import DeleteDialog from '../../common/DeleteDialog/DeleteDialog';
import { handleSendMessage } from '../../../utils/Chat/index';
import ErrorSnackbar from '../../common/ErrorSnackbar/ErrorSnackbar';
import { useChatTabsContext } from '../../../contexts/ChatTabsContext';
import {
  CustomTextField,
} from './styles/ChatTextField.styles';
import EmptyChatState from './EmptyChatState';
import { ChatContainer } from './styles/ChatContainer.styles';
import {
  CustomCloseIconButton,
  PrimaryCircularProgress,
  PrimaryHistoryIcon,
  PrimaryRobotIcon,
  WhiteIoArrowUp,
} from './styles/ChatIcons.styles';
import { ChatText } from './styles/ChatText.styles';
import { DeleteIconButton, SendMessageButton } from './styles/ChatButtons.styles';

const ChatComponent: React.FC<ChatComponentProps> = ({ topic, isOpen, onClose }): JSX.Element => {
  const { t } = useTranslation('chat');
  const {
    tabs,
    setTabs,
    activeTab,
    setActiveTab,
    deleteDialogOpen,
    handleConfirmDelete,
    handleCancelDelete,
    handleCloseTab,
    truncateTopic,
  } = useChatTabsContext();

  const [newMessage, setNewMessage] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isOpen) {
      window.dispatchEvent(new Event('mainChatOpen'));
    } else {
      window.dispatchEvent(new Event('mainChatClose'));
    }
  }, [isOpen]);

  useEffect(() => {
    if (topic && !tabs.find(tab => tab.topic === topic)) {
      const messages = getMessagesForTopic(topic);
      const messagesWithTimestamps = messages.map(msg => ({
        ...msg,
        timestamp: msg.timestamp || new Date(),
      }));

      const optionId = parseInt(localStorage.getItem('currentOptionId') || '0');

      const newTab: Tab = {
        id: Date.now().toString(),
        topic,
        messages: messagesWithTimestamps,
        optionId,
      };

      setTabs(prev => [newTab, ...prev]);
      setActiveTab(newTab.id);
    } else if (topic) {
      const existingTab = tabs.find(tab => tab.topic === topic);
      if (existingTab) {
        setActiveTab(existingTab.id);
      }
    }
  }, [topic]);

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSendMessage({
        newMessage,
        activeTab,
        tabs,
        setTabs,
        setNewMessage,
        setLoading,
        setError,
        truncateTopic,
        id: Date.now(),
        optionId: parseInt(activeTab),
      });
    }
  };

  const handleTabClick = (tabId: string) => {
    setActiveTab(tabId);
    const activeTabData = tabs.find(tab => tab.id === tabId);
    if (activeTabData?.optionId) {
      localStorage.setItem('currentOptionId', activeTabData.optionId.toString());
    }
  };

  const currentMessages = useMemo(
    () => tabs.find(tab => tab.id === activeTab)?.messages || [],
    [tabs, activeTab],
  );

  const scrollToBottom = useCallback((behavior: ScrollBehavior = 'smooth') => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior, block: 'end' });
    }
  }, []);

  useEffect(() => {
    if (currentMessages.length > 0) {
      requestAnimationFrame(() => {
        scrollToBottom('smooth');
      });
    }
  }, [currentMessages.length, scrollToBottom]);

  useEffect(() => {
    if (activeTab) {
      requestAnimationFrame(() => {
        scrollToBottom('auto');
      });
    }
  }, [activeTab, scrollToBottom]);

  return (
    <>
      <ChatContainer
        elevation={3}
        className={isOpen ? 'visible' : ''}
        isOpen={isOpen}
        optVariant="main"
      >
        <ChatContainer optVariant="inner">
          <ChatContainer optVariant="chatModalHeader">
            <ChatText optVariant="chatModalHeader">
              {t('main.title')}
            </ChatText>
            <ChatContainer optVariant="chatModalHeaderIcon">
              <CustomCloseIconButton
                onClick={onClose}
                size="small"
              >
                <Close />
              </CustomCloseIconButton>
            </ChatContainer>
          </ChatContainer>

          <ChatContainer optVariant="body">
            <ChatContainer optVariant="tabs">
              <ChatContainer optVariant="chatsHeader">
                <PrimaryHistoryIcon />
                <ChatText optVariant="chatsTitle">
                  {t('main.allChats')}
                </ChatText>
              </ChatContainer>
              <ChatContainer optVariant="chatsInner">
                {tabs.map(tab => (
                  <ChatContainer optVariant="chatsItem"
                    key={tab.id}
                    isActive={activeTab === tab.id}
                    onClick={() => handleTabClick(tab.id)}
                  >
                    <ChatContainer optVariant="chatsItemInner" isActive={activeTab === tab.id}>
                      <ChatText optVariant="chatsTabTitle" isActive={activeTab === tab.id}>
                        {truncateTopic(tab.topic, { xs: 20, md: 25 })}
                      </ChatText>
                      <DeleteIconButton
                        onClick={(e) => {
                          e.stopPropagation();
                          handleCloseTab(tab.id);
                        }}
                      />
                    </ChatContainer>
                  </ChatContainer>
                ))}
              </ChatContainer>
            </ChatContainer>

            <ChatContainer optVariant="tabContent">
              <ChatContainer optVariant="tabContentHeader">
                <PrimaryRobotIcon />
                <ChatText optVariant="tabContentTitle">
                  {t('main.chatAssistant')}
                </ChatText>
              </ChatContainer>
              {tabs.length === 0 ? (
                <EmptyChatState />
              ) : (
                <>
                  <ChatContainer optVariant="messages">
                    {tabs.find(tab => tab.id === activeTab)?.messages.slice(1).map((message) => (
                      <ChatContainer optVariant="messageBubble"
                        key={message.id}
                        isCurrentUser={message.isCurrentUser}
                        role="listitem"
                        aria-label={t('main.messageFrom', { sender: message.isCurrentUser ? 'you' : 'sender' })}
                      >
                        <ChatContainer optVariant="messageContent" isCurrentUser={message.isCurrentUser}>
                          <ChatText optVariant="messageText" isCurrentUser={message.isCurrentUser}>
                            {message.text}
                          </ChatText>
                          {message.timestamp && (
                            <ChatText optVariant="messageTimestamp" isCurrentUser={message.isCurrentUser}>
                              {message.timestamp.toLocaleString([], {
                                month: 'short',
                                day: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit',
                              })}
                            </ChatText>
                          )}
                        </ChatContainer>
                      </ChatContainer>
                    ))}
                    <div ref={messagesEndRef} />
                  </ChatContainer>

                  <ChatContainer optVariant="input">
                    <CustomTextField
                      fullWidth
                      variant="outlined"
                      placeholder={t('messages.placeholder')}
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      onKeyPress={handleKeyPress}
                      multiline
                      maxRows={4}
                      size="small"
                      aria-label="Message input"
                    />
                    <ChatContainer optVariant="inputButton">
                      {newMessage.trim() && (
                        <SendMessageButton
                          onClick={() => handleSendMessage({
                            newMessage, activeTab, tabs,
                            setTabs, setNewMessage, setLoading,
                            setError, truncateTopic,
                            id: Date.now(),
                            optionId: parseInt(activeTab),
                          })}
                          disableRipple
                          aria-label="Send message"
                          disabled={loading}
                        >
                          {loading ? (
                            <PrimaryCircularProgress size={20} />
                          ) : (
                            <WhiteIoArrowUp size={20} />
                          )}
                        </SendMessageButton>
                      )}
                    </ChatContainer>
                  </ChatContainer>
                </>
              )}
            </ChatContainer>
          </ChatContainer>
        </ChatContainer>
      </ChatContainer>

      <DeleteDialog
        open={deleteDialogOpen}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
      />

      <ErrorSnackbar error={error} onClose={() => setError('')} />
    </>
  );
};

export default ChatComponent;